import React, { useState, useEffect } from 'react';
import {
  Mail, ChevronDown, ChevronUp, Save, RotateCcw, Search,
  Tag, Edit3, Loader2, Check, AlertTriangle, Info, Layout, Eye
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  authentication: 'bg-blue-100 text-blue-800',
  tier_verification: 'bg-orange-100 text-orange-800',
  company: 'bg-green-100 text-green-800',
  support: 'bg-purple-100 text-purple-800',
  referral: 'bg-pink-100 text-pink-800',
  admin_notification: 'bg-red-100 text-red-800',
  quotes: 'bg-indigo-100 text-indigo-800',
  access: 'bg-teal-100 text-teal-800',
  invites: 'bg-amber-100 text-amber-800',
};

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(null);

  // Frame editor state
  const [frameHtml, setFrameHtml] = useState('');
  const [frameOriginal, setFrameOriginal] = useState('');
  const [frameCustomized, setFrameCustomized] = useState(false);
  const [frameTags, setFrameTags] = useState([]);
  const [frameLoading, setFrameLoading] = useState(true);
  const [frameSaving, setFrameSaving] = useState(false);
  const [showFrameEditor, setShowFrameEditor] = useState(false);
  const [showFramePreview, setShowFramePreview] = useState(false);
  // Frame email settings
  const [frameFromEmail, setFrameFromEmail] = useState('');
  const [frameReplyTo, setFrameReplyTo] = useState('');
  const [frameBcc, setFrameBcc] = useState('');
  // Original email settings (to track changes)
  const [frameFromEmailOrig, setFrameFromEmailOrig] = useState('');
  const [frameReplyToOrig, setFrameReplyToOrig] = useState('');
  const [frameBccOrig, setFrameBccOrig] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchFrame();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/email-templates');
      setTemplates(res.data);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchFrame = async () => {
    try {
      const res = await api.get('/email-templates/frame');
      setFrameHtml(res.data.body_html || '');
      setFrameOriginal(res.data.body_html || '');
      setFrameCustomized(res.data.is_customized || false);
      setFrameTags(res.data.available_tags || []);
      setFrameFromEmail(res.data.from_email || '');
      setFrameReplyTo(res.data.reply_to_email || '');
      setFrameBcc(res.data.bcc_emails || '');
      setFrameFromEmailOrig(res.data.from_email || '');
      setFrameReplyToOrig(res.data.reply_to_email || '');
      setFrameBccOrig(res.data.bcc_emails || '');
    } catch (error) {
      toast.error('Failed to load email frame template');
    } finally {
      setFrameLoading(false);
    }
  };

  const handleSaveFrame = async () => {
    if (!frameHtml.includes('[email_content]')) {
      toast.error('Frame template must contain the [email_content] tag');
      return;
    }
    setFrameSaving(true);
    try {
      await api.put('/email-templates/frame', {
        body_html: frameHtml,
        from_email: frameFromEmail,
        reply_to_email: frameReplyTo,
        bcc_emails: frameBcc,
      });
      toast.success('Email frame saved successfully');
      setFrameOriginal(frameHtml);
      setFrameFromEmailOrig(frameFromEmail);
      setFrameReplyToOrig(frameReplyTo);
      setFrameBccOrig(frameBcc);
      setFrameCustomized(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save frame');
    } finally {
      setFrameSaving(false);
    }
  };

  const handleResetFrame = async () => {
    if (!window.confirm('Reset the email frame to its default? Your customizations will be lost.')) return;
    try {
      const res = await api.post('/email-templates/frame/reset');
      setFrameHtml(res.data.body_html || '');
      setFrameOriginal(res.data.body_html || '');
      setFrameCustomized(false);
      setFrameFromEmail('');
      setFrameReplyTo('');
      setFrameBcc('');
      setFrameFromEmailOrig('');
      setFrameReplyToOrig('');
      setFrameBccOrig('');
      toast.success('Email frame reset to default');
    } catch (error) {
      toast.error('Failed to reset frame');
    }
  };

  const toggleExpand = (key) => {
    if (expandedTemplate === key) {
      setExpandedTemplate(null);
    } else {
      setExpandedTemplate(key);
      // Pre-fill edit data
      const tmpl = templates.find(t => t.template_key === key);
      if (tmpl) {
        setEditData({
          subject: tmpl.subject,
          body_html: tmpl.body_html
        });
      }
    }
  };

  const handleSave = async (templateKey) => {
    setSaving(templateKey);
    try {
      await api.put(`/email-templates/${templateKey}`, editData);
      toast.success('Template saved successfully');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (templateKey) => {
    if (!window.confirm('Reset this template to its default content? Your customizations will be lost.')) return;
    try {
      await api.post(`/email-templates/${templateKey}/reset`);
      toast.success('Template reset to default');
      setExpandedTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const categories = [...new Set(templates.map(t => t.category))];

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !searchTerm ||
      t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.template_key.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <Sidebar>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="w-7 h-7 text-blue-600" />
              Email Templates
            </h1>
            <p className="text-gray-500 mt-1">Manage all system email messages sent to users during various processes</p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-800">Available Tags</h3>
              <p className="text-sm text-blue-700 mt-1">
                Use these tags in your templates and they will be replaced with actual values when the email is sent:
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['[firstname]', '[lastname]', '[email]', '[user_email]', '[adminemail]', '[parentname]', '[company_name]', '[date]', '[tier_status]', '[rejection_reason]', '[status]'].map(tag => (
                  <code key={tag} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-mono">{tag}</code>
                ))}
              </div>
              <p className="text-sm text-blue-700 mt-3 font-semibold">Global Tags (available in ALL templates):</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {['[trados_logo]', '[company_logo]', '[company_name]', '[company_url]', '[company_email]'].map(tag => (
                  <code key={tag} className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-mono">{tag}</code>
                ))}
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Global tags resolve automatically based on the user's company context. Use <code>[trados_logo]</code> for the Trados logo URL and <code>[company_logo]</code> for the company's logo.
              </p>
            </div>
          </div>
        </div>

        {/* Email Frame Template Editor */}
        <div className="bg-white rounded-lg border mb-6 overflow-hidden">
          <button
            onClick={() => setShowFrameEditor(!showFrameEditor)}
            className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition"
          >
            <Layout className="w-5 h-5 text-indigo-600" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">Email Frame Template</span>
                {frameCustomized && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Customized</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">The outer wrapper/frame used by ALL emails. Edit the layout that surrounds every email's content.</p>
            </div>
            {showFrameEditor ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showFrameEditor && (
            <div className="border-t p-4 bg-gray-50">
              {frameLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {/* Frame Tags */}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Available Frame Tags</label>
                    <div className="flex flex-wrap gap-1.5">
                      {frameTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => {
                            navigator.clipboard.writeText(tag);
                            toast.success(`Copied ${tag}`);
                          }}
                          className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-mono hover:bg-indigo-200 transition cursor-pointer"
                          title="Click to copy"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      <strong>[email_content]</strong> is required — it marks where each email's body content will be inserted.
                    </p>
                  </div>

                  {/* Frame Email Settings */}
                  <div className="mb-4 p-4 bg-white border rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Email Settings</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      These settings apply to ALL outgoing emails. You can use tags like <code className="bg-gray-100 px-1 rounded">[company_email]</code> which will resolve per company context.
                      Separate multiple addresses with commas.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From Email</label>
                        <input
                          type="text"
                          value={frameFromEmail}
                          onChange={(e) => setFrameFromEmail(e.target.value)}
                          placeholder="e.g. noreply@trados.app"
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="text-xs text-gray-400 mt-0.5">Leave empty to use SMTP default</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Reply-To Email</label>
                        <input
                          type="text"
                          value={frameReplyTo}
                          onChange={(e) => setFrameReplyTo(e.target.value)}
                          placeholder="e.g. [company_email]"
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="text-xs text-gray-400 mt-0.5">Supports tags like [company_email]</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">BCC Emails</label>
                        <input
                          type="text"
                          value={frameBcc}
                          onChange={(e) => setFrameBcc(e.target.value)}
                          placeholder="e.g. admin@trados.app, [company_email]"
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="text-xs text-gray-400 mt-0.5">Comma-separated, supports tags</p>
                      </div>
                    </div>
                  </div>

                  {/* Frame HTML Editor */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frame HTML</label>
                    <textarea
                      value={frameHtml}
                      onChange={(e) => setFrameHtml(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                      rows={14}
                    />
                  </div>

                  {/* Preview Toggle */}
                  <div className="mb-4">
                    <button
                      onClick={() => setShowFramePreview(!showFramePreview)}
                      className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      {showFramePreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                    {showFramePreview && (
                      <div
                        className="bg-white border rounded-lg p-4 mt-2 text-sm"
                        dangerouslySetInnerHTML={{
                          __html: frameHtml
                            .replace('[email_content]', '<div style="padding:20px;background:#e5e7eb;border-radius:8px;text-align:center;color:#6b7280;">[Your email content appears here]</div>')
                            .replaceAll('[trados_logo]', 'https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-square.png')
                            .replaceAll('[company_logo]', 'https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified-square.png')
                            .replaceAll('[company_name]', 'Trados')
                            .replaceAll('[company_url]', 'https://www.trados.app')
                            .replaceAll('[company_email]', 'noreply@trados.app')
                            .replaceAll('[year]', new Date().getFullYear().toString())
                        }}
                      />
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 justify-end">
                    {frameCustomized && (
                      <button
                        onClick={handleResetFrame}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Reset to Default
                      </button>
                    )}
                    <button
                      onClick={handleSaveFrame}
                      disabled={frameSaving || (frameHtml === frameOriginal && frameFromEmail === frameFromEmailOrig && frameReplyTo === frameReplyToOrig && frameBcc === frameBccOrig)}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
                    >
                      {frameSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Frame
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No templates found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTemplates.map((template) => {
              const isExpanded = expandedTemplate === template.template_key;
              const catColor = CATEGORY_COLORS[template.category] || 'bg-gray-100 text-gray-800';

              return (
                <div key={template.template_key} className="bg-white rounded-lg border overflow-hidden">
                  {/* Template Header - Click to Expand */}
                  <button
                    onClick={() => toggleExpand(template.template_key)}
                    className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition"
                  >
                    <div className="flex-shrink-0">
                      <Mail className={`w-5 h-5 ${template.is_customized ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 text-sm">{template.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColor}`}>
                          {template.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        {template.is_customized && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            Customized
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </button>

                  {/* Expanded Edit Section */}
                  {isExpanded && (
                    <div className="border-t p-4 bg-gray-50">
                      {/* Available Tags */}
                      <div className="mb-4">
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Available Tags for this Template</label>
                        <div className="flex flex-wrap gap-1.5">
                          {(template.available_tags || []).map(tag => (
                            <button
                              key={tag}
                              onClick={() => {
                                // Copy tag to clipboard
                                navigator.clipboard.writeText(tag);
                                toast.success(`Copied ${tag}`);
                              }}
                              className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono hover:bg-gray-300 transition cursor-pointer"
                              title="Click to copy"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                        <input
                          type="text"
                          value={editData.subject || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, subject: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Body HTML */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Body (HTML)</label>
                        <textarea
                          value={editData.body_html || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, body_html: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                          rows={12}
                        />
                      </div>

                      {/* Preview */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                        <div
                          className="bg-white border rounded-lg p-4 text-sm"
                          dangerouslySetInnerHTML={{ __html: editData.body_html || '' }}
                        />
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 justify-end">
                        {template.is_customized && (
                          <button
                            onClick={() => handleReset(template.template_key)}
                            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Reset to Default
                          </button>
                        )}
                        <button
                          onClick={() => handleSave(template.template_key)}
                          disabled={saving === template.template_key}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                        >
                          {saving === template.template_key ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Save Template
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminEmailTemplates;
